/**
 * Sheet page bundle: all pages of one raster tier in ONE file, so opening a song is one request
 * and a connector jump never needs the network.
 *
 *   offset 0   'KGRB'            magic (4 bytes)
 *   offset 4   u32 LE version    (1)
 *   offset 8   u32 LE headerLen
 *   offset 12  header JSON (utf-8): BundleHeader
 *   then       page image bytes; page.offset is relative to the start of this data section
 */
import type { SheetTier } from './dto/library';

export const BUNDLE_MAGIC = 'KGRB';
export const BUNDLE_VERSION = 1;
export const BUNDLE_PREFIX = 12;

export interface BundlePage {
  /** Page size in PDF points (aspect ratio). */
  w: number;
  h: number;
  /** Raster pixel size of this tier's image. */
  pw: number;
  ph: number;
  type: 'image/webp' | 'image/jpeg' | 'image/png';
  offset: number;
  len: number;
}

export interface BundleHeader {
  v: 1;
  tier: SheetTier;
  width: number;
  pages: BundlePage[];
}

/** Build a bundle Blob from encoded page images (rasteriser side). */
export function packBundle(tier: SheetTier, width: number, pages: { w: number; h: number; pw: number; ph: number; blob: Blob }[]): Blob {
  let offset = 0;
  const header: BundleHeader = {
    v: 1,
    tier,
    width,
    pages: pages.map((p) => {
      const entry: BundlePage = { w: p.w, h: p.h, pw: p.pw, ph: p.ph, type: p.blob.type as BundlePage['type'], offset, len: p.blob.size };
      offset += p.blob.size;
      return entry;
    }),
  };
  const json = new TextEncoder().encode(JSON.stringify(header));
  const prefix = new Uint8Array(BUNDLE_PREFIX);
  prefix.set([0x4b, 0x47, 0x52, 0x42], 0); // 'KGRB'
  const dv = new DataView(prefix.buffer);
  dv.setUint32(4, BUNDLE_VERSION, true);
  dv.setUint32(8, json.byteLength, true);
  return new Blob([prefix, json, ...pages.map((p) => p.blob)], { type: 'application/octet-stream' });
}

/** Parse the 12-byte prefix; returns header length or throws. */
export function readPrefix(bytes: Uint8Array): number {
  if (bytes.length < BUNDLE_PREFIX) throw new Error('bundle too short');
  if (bytes[0] !== 0x4b || bytes[1] !== 0x47 || bytes[2] !== 0x52 || bytes[3] !== 0x42) throw new Error('not a KGRB bundle');
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const v = dv.getUint32(4, true);
  if (v !== BUNDLE_VERSION) throw new Error(`unsupported bundle version ${v}`);
  const len = dv.getUint32(8, true);
  if (len <= 0 || len > 4_000_000) throw new Error('bad header length');
  return len;
}

export function parseHeader(json: Uint8Array): BundleHeader {
  const h = JSON.parse(new TextDecoder().decode(json)) as BundleHeader;
  if (h.v !== 1 || !Array.isArray(h.pages)) throw new Error('bad bundle header');
  return h;
}

/** Split a fully downloaded bundle into per-page Blobs (zero-copy slices). */
export async function unpackBundle(bundle: Blob): Promise<{ header: BundleHeader; pages: Blob[] }> {
  const prefix = new Uint8Array(await bundle.slice(0, BUNDLE_PREFIX).arrayBuffer());
  const hlen = readPrefix(prefix);
  const header = parseHeader(new Uint8Array(await bundle.slice(BUNDLE_PREFIX, BUNDLE_PREFIX + hlen).arrayBuffer()));
  const base = BUNDLE_PREFIX + hlen;
  const pages = header.pages.map((p) => bundle.slice(base + p.offset, base + p.offset + p.len, p.type));
  return { header, pages };
}
